import React from 'react';
import styles from './FormulaTooltip.module.css';
import { useTooltip } from '../contexts/TooltipContext';

interface FormulaTooltipProps {
  /** The formula text to display (e.g., "Downtime Loss / Hard-Cem Material Cost") */
  formula: string;
  
  /** Optional expanded formula explanation */
  expandedFormula?: string;
  
  /** Optional additional note (e.g., for zero-incident scenarios) */
  note?: string;
  
  /** Unique ID for accessibility */
  id: string;
  
  /** Aria label for the icon */
  ariaLabel: string;
  
  /** Optional CSS class for custom styling */
  className?: string;
}

const FormulaTooltip: React.FC<FormulaTooltipProps> = ({
  formula,
  expandedFormula,
  note,
  id,
  ariaLabel,
  className = ''
}) => {
  // Use global tooltip context to ensure only one tooltip is visible at a time
  const { activeTooltipId, setActiveTooltip } = useTooltip();
  const isVisible = activeTooltipId === id;
  const tooltipRef = React.useRef<HTMLSpanElement>(null);
  const tooltipContentRef = React.useRef<HTMLDivElement>(null);
  const isClickingRef = React.useRef(false);
  const [tooltipPosition, setTooltipPosition] = React.useState<{
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
    transform?: string;
  }>({});

  // Validate required props
  if (!formula || !id || !ariaLabel || formula.trim() === '' || id.trim() === '' || ariaLabel.trim() === '') {
    console.warn('FormulaTooltip: Missing required props (formula, id, or ariaLabel)');
    return null;
  }

  // Handle empty formula string
  const displayFormula = formula.trim();

  // Click-outside detection for touch devices
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setActiveTooltip(null);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isVisible, setActiveTooltip]);

  // Viewport boundary detection and positioning with fixed positioning
  React.useEffect(() => {
    if (!isVisible || !tooltipContentRef.current || !tooltipRef.current) {
      return;
    }

    const adjustTooltipPosition = () => {
      const tooltipContent = tooltipContentRef.current;
      const container = tooltipRef.current;
      
      if (!tooltipContent || !container) return;

      const containerRect = container.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Get tooltip dimensions (it needs to be visible to measure)
      // Use a temporary visibility to measure if needed
      const wasHidden = tooltipContent.style.visibility === 'hidden';
      if (wasHidden) {
        tooltipContent.style.visibility = 'hidden';
        tooltipContent.style.opacity = '0';
        tooltipContent.style.display = 'block';
      }
      
      const tooltipWidth = tooltipContent.offsetWidth || 250; // fallback width
      const tooltipHeight = tooltipContent.offsetHeight || 100; // fallback height
      
      if (wasHidden) {
        tooltipContent.style.display = '';
      }
      
      const newPosition: {
        position?: string;
        top?: string;
        bottom?: string;
        left?: string;
        right?: string;
        transform?: string;
        marginBottom?: string;
      } = {};

      // Check if tooltip would overflow viewport
      const spaceAbove = containerRect.top;
      const spaceBelow = viewportHeight - containerRect.bottom;
      const tooltipHeightWithMargin = tooltipHeight + 8;
      
      // Calculate horizontal position
      const iconCenterX = containerRect.left + containerRect.width / 2;
      let tooltipLeft = iconCenterX - tooltipWidth / 2;
      
      const margin = 16;
      const wouldOverflowLeft = tooltipLeft < margin;
      const wouldOverflowRight = tooltipLeft + tooltipWidth > viewportWidth - margin;
      const wouldOverflowTop = spaceAbove < tooltipHeightWithMargin;
      
      // Only use fixed positioning if we need viewport boundary detection
      if (wouldOverflowLeft || wouldOverflowRight || wouldOverflowTop) {
        newPosition.position = 'fixed';
        
        // Calculate horizontal position
        if (wouldOverflowLeft) {
          tooltipLeft = margin;
        } else if (wouldOverflowRight) {
          tooltipLeft = viewportWidth - tooltipWidth - margin;
        }
        
        newPosition.left = `${tooltipLeft}px`;
        newPosition.transform = 'none';
        newPosition.marginBottom = '0';

        // Calculate vertical position
        if (wouldOverflowTop) {
          newPosition.top = `${containerRect.bottom + 8}px`;
        } else {
          newPosition.top = `${containerRect.top - tooltipHeight - 8}px`;
        }
      }
      // Otherwise, let CSS handle positioning (absolute positioning relative to container)

      setTooltipPosition(newPosition);
    };

    // Adjust position immediately when tooltip becomes visible
    adjustTooltipPosition();

    // Re-adjust on window resize and scroll
    window.addEventListener('resize', adjustTooltipPosition);
    window.addEventListener('scroll', adjustTooltipPosition, true);

    return () => {
      window.removeEventListener('resize', adjustTooltipPosition);
      window.removeEventListener('scroll', adjustTooltipPosition, true);
    };
  }, [isVisible]);

  // Hover event handlers
  const handleMouseEnter = () => {
    setActiveTooltip(id);
  };

  const handleMouseLeave = () => {
    setActiveTooltip(null);
  };

  // Focus event handlers - only for keyboard navigation
  const handleFocus = () => {
    // Only show tooltip on keyboard focus, not mouse click focus
    if (!isClickingRef.current) {
      setActiveTooltip(id);
    }
  };

  const handleBlur = () => {
    setActiveTooltip(null);
    isClickingRef.current = false;
  };

  // Touch/click event handler - fires before focus
  const handleMouseDown = (event: React.MouseEvent) => {
    // Don't prevent default on desktop - let hover work naturally
    // Only handle click for touch devices
    if ('ontouchstart' in window) {
      event.preventDefault();
      isClickingRef.current = true;
      setActiveTooltip(isVisible ? null : id);
    } else {
      // On desktop, just mark that we're clicking to prevent focus from showing tooltip
      isClickingRef.current = true;
    }
  };

  return (
    <span 
      className={`${styles.tooltipContainer} ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={tooltipRef}
    >
      <button
        type="button"
        className={styles.tooltipIcon}
        aria-label={ariaLabel}
        aria-describedby={id}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMouseDown={handleMouseDown}
      >
        ?
      </button>
      <div
        id={id}
        className={`${styles.tooltipContent} ${isVisible ? styles.visible : ''}`}
        role="tooltip"
        aria-hidden={!isVisible}
        ref={tooltipContentRef}
        style={tooltipPosition}
      >
        <div className={styles.formula}>{displayFormula}</div>
        {expandedFormula && (
          <div className={styles.expandedFormula}>{expandedFormula}</div>
        )}
        {note && (
          <div className={styles.note}>{note}</div>
        )}
      </div>
    </span>
  );
};

export default FormulaTooltip;
